import { forwardRef, HtmlHTMLAttributes, memo, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Button } from "../../Button";
import { Overlay } from "../../Overlay";
import { Scrollbars } from "../../Scrollbars";
import { Tooltip } from "../../Tooltip";
import { NavItem } from "../NavItem";
import { NavMenuButton } from "../NavMenuButton";
import { MenuItemDescription } from "../types";
import { AppNavigationControlContext } from "./AppNavigationControlContext";
import { AppNavigationState, AppNavigationStateContextProvider } from "./AppNavigationStateContext";

import { ReactComponent as ChevronLeft } from "./icons/chevron-left.svg";
import { ReactComponent as ChevronRight } from "./icons/chevron-right.svg";

import styles from "./styles.module.scss";

export type AppNavigationProps = HtmlHTMLAttributes<HTMLDivElement> & {
  logo?: ReactNode,
  menuItems?: MenuItemDescription[]
  beforeMenu?: ReactNode | ReactNode[],
  afterMenu?: ReactNode | ReactNode[],
  userInfo?: ReactNode | ReactNode[],
  hideCategories?: boolean
} & (
  {
    type: "header" | "mobile",
    multiTier?: never
    minimizable?: never
    defaultMinimized?: never
    expandOnHover?: never
  } |
  {
    type: "sidebar",
    multiTier?: boolean,
    minimizable?: boolean
    defaultMinimized?: boolean
    expandOnHover?: boolean
  })

export const AppNavigation = memo(forwardRef<HTMLDivElement, AppNavigationProps>((
  {
    type,
    className,
    hideCategories,
    logo,
    menuItems,
    beforeMenu,
    afterMenu,
    userInfo,
    multiTier = false,
    minimizable = true,
    defaultMinimized = true,
    expandOnHover = false,
    ...props
  },
  ref,
) => {
  const [ isOpen, setIsOpen ] = useState(false);
  const [ isExpanded, setIsExpanded ] = useState(false);
  const [ isMinimized, setIsMinimized ] = useState(defaultMinimized);
  const menuTiersRef = useRef<HTMLDivElement>(null);

  const appNavigationState: Partial<AppNavigationState> = useMemo(() => ({
    type,
    isOpen,
    isExpanded,
    isMinimized: minimizable && isMinimized,
    showTooltip: type === "sidebar" && minimizable && isMinimized && !expandOnHover,
    tooltipPlacement: "right",
  }), [ type, isOpen, isExpanded, minimizable, isMinimized, expandOnHover ]);
  const useMenuTiers = type === "header" || (type === "sidebar" && (multiTier || (minimizable && isMinimized && !expandOnHover)));

  const getPortalNode = useCallback((level: number) => {
    if (useMenuTiers) {
      if (level > 1) {
        const children = menuTiersRef.current?.children;
        return children?.[Math.min(level - 2, children.length - 1)] || null;
      }
    }
    return null;
  }, [ useMenuTiers ]);

  return (
    <AppNavigationControlContext.Provider value={{ setMenuIsOpen: setIsOpen }}>
      <AppNavigationStateContextProvider value={appNavigationState}>
        <div
          className={classNames(
            className,
            styles.root,
            {
              [styles.header]: type === "header",
              [styles.sidebar]: type === "sidebar",
              [styles.mobile]: type === "mobile",
              [styles.hideCategories]: hideCategories,
              [styles.multiTier]: useMenuTiers,
              [styles.minimizable]: minimizable,
              [styles.expandOnHover]: expandOnHover,
            },
          )}
          ref={ref}
          {...props}
          onPointerEnter={event => {
            props.onPointerEnter?.(event);
            if (minimizable && expandOnHover) {
              setIsExpanded(true);
            }
          }}
          onPointerLeave={event => {
            props.onPointerLeave?.(event);
            if (minimizable && expandOnHover) {
              setIsExpanded(false);
            }
          }}
          data-menu-open={isOpen}
          data-menu-minimized={isMinimized}
          data-menu-hovered={isExpanded}
        >
          {
            (logo || type === "mobile") &&
            <div className={styles.logo}>
              {logo}
            </div>
          }
          {
            type === "mobile" &&
            <>
              <NavMenuButton
                className={styles.burgerButton}
                isMenuOpen={isOpen}
                onClick={() => setIsOpen(prevState => !prevState)}
              />
            </>
          }
          {
            (type !== "mobile" || isOpen) &&
            <div className={styles.menuContainer}>
              <div className={styles.menuContent}>
                <Scrollbars
                  className={classNames(styles.menuList, "os-host-flexbox")}
                  options={{
                    autoUpdate: minimizable,
                    autoUpdateInterval: 1000,
                    overflowBehavior: {
                      x: type !== "header" ? "hidden" : "scroll",
                      y: type === "header" ? "hidden" : "scroll",
                    },
                  }}
                >
                  <NavItem
                    id={"menu-root"}
                    label={""}
                    items={menuItems}
                    level={0}
                    defaultIsOpen
                    getPortalNode={getPortalNode}
                  />
                </Scrollbars>
                <AppNavigationStateContextProvider value={{
                  menuItemMinimized: type === "header",
                  showTooltip: type === "header",
                  tooltipPlacement: type === "header" ? "bottom" : undefined,
                }}>
                  {
                    beforeMenu &&
                    <div className={styles.beforeMenu}>
                      {beforeMenu}
                    </div>
                  }
                  {
                    afterMenu &&
                    <div className={styles.afterMenu}>
                      {afterMenu}
                    </div>
                  }
                  {
                    userInfo &&
                    <div className={styles.footer}>
                      {userInfo}
                    </div>
                  }
                </AppNavigationStateContextProvider>
              </div>
              {type === "mobile" &&
                <Overlay
                  backgroundType="blurred"
                  className={styles.overlay}
                />
              }
              {
                (type === "sidebar" && minimizable) &&
                <Tooltip content="Expand or collapse the navigation">
                  <Button
                    className={styles.minimizeButton}
                    buttonType="secondaryGray"
                    buttonSize="xs"
                    type="button"
                    onClick={() => setIsMinimized(prevState => !prevState)}
                  >
                    {isMinimized ? <ChevronRight /> : <ChevronLeft />}
                  </Button>
                </Tooltip>
              }
            </div>
          }
          {
            useMenuTiers &&
            <div
              className={styles.subTiers}
              ref={menuTiersRef}
            >
              {
                Array.from({ length: 4 }, v => null)
                  .map((x, i) => <div key={i} className={styles.menuTier} />)
              }
            </div>
          }
        </div>
      </AppNavigationStateContextProvider>
    </AppNavigationControlContext.Provider>
  );
}));